import {
  getSavedSizeSelectionId,
  getSizeDimensionsLabel,
  getSizeFormatId,
  resolveSizeSetSelectionIds,
  type SavedSize,
  type SizeSet,
  SOCIAL_MEDIA_PLATFORM_GROUPS,
} from '@air/feature-saved-sizes/utils';

import { type CustomSize } from '../../hooks/useCustomSizes';
import { CUSTOM_PLATFORM_LABEL, type SmartResizeFormat } from './smartResizeShared';

/** Every platform format the image Smart resize picker offers. */
export const ALL_PLATFORM_FORMAT_KEYS = new Set(
  SOCIAL_MEDIA_PLATFORM_GROUPS.flatMap((group) => group.formats.map((format) => format.id)),
);

export type BuildSmartResizeFormatsForSelectionParams = {
  customSizes: CustomSize[];
  selectedFormatIds: ReadonlySet<string>;
  sizePresets: SavedSize[];
};

export type SmartResizeSelectedFormats = {
  /** Saved + session-custom sizes deduped by payload id -- the analytics `custom_size_count`, since it must count outputs, not selected chips. */
  customAndSavedFormatCount: number;
  /** Saved + session-custom sizes deduped by payload id, then combined with platform presets. */
  formats: SmartResizeFormat[];
  presetFormats: SmartResizeFormat[];
};

/**
 * Image-only: the video picker builds a different format shape and merges saved sizes before
 * session-custom ones, so it must not share this.
 */
export const buildSmartResizeFormatsForSelection = ({
  customSizes,
  selectedFormatIds,
  sizePresets,
}: BuildSmartResizeFormatsForSelectionParams): SmartResizeSelectedFormats => {
  const presetFormats = SOCIAL_MEDIA_PLATFORM_GROUPS.flatMap((group) =>
    group.formats
      .filter((format) => selectedFormatIds.has(format.id))
      .map((format) => ({ format: format.label, formatId: format.id, platform: group.label })),
  );

  const savedSelectedFormats = sizePresets
    .filter((sizePreset) => selectedFormatIds.has(getSavedSizeSelectionId(sizePreset.id)))
    .map((sizePreset) => ({
      format: `${sizePreset.name} (${getSizeDimensionsLabel(sizePreset)} px)`,
      formatId: getSizeFormatId(sizePreset),
      platform: CUSTOM_PLATFORM_LABEL,
      savedSizeName: sizePreset.name,
    }));

  const customSelectedFormats = customSizes
    .filter((format) => selectedFormatIds.has(format.id))
    .map((format) => ({ format: `${format.label} px`, formatId: format.id, platform: CUSTOM_PLATFORM_LABEL }));

  // Saved and session-custom sizes both resolve to a `custom_WxH` payload id, so two distinct
  // selections sharing pixels collapse to one entry here (the saved size, listed last, keeps its
  // name) -- platform presets (a disjoint id space) are never affected.
  const customAndSavedFormatsById = new Map(
    [...customSelectedFormats, ...savedSelectedFormats].map((format) => [format.formatId, format]),
  );

  return {
    customAndSavedFormatCount: customAndSavedFormatsById.size,
    formats: [...presetFormats, ...customAndSavedFormatsById.values()],
    presetFormats,
  };
};

/** The formats the image picker would generate with exactly this set selected. Deleted members are skipped. */
export const getSmartResizeFormatsForSizeSet = (sizeSet: SizeSet, sizePresets: SavedSize[]): SmartResizeFormat[] => {
  const selectionIds = resolveSizeSetSelectionIds(sizeSet.members, {
    savedSizesById: new Map(sizePresets.map((sizePreset) => [sizePreset.id, sizePreset])),
    selectablePlatformKeys: ALL_PLATFORM_FORMAT_KEYS,
  });

  return buildSmartResizeFormatsForSelection({
    customSizes: [],
    selectedFormatIds: new Set(selectionIds),
    sizePresets,
  }).formats;
};
